// The live transcript of a recording in progress, like the app's LiveTranscriber
// (app/.../transcription/LiveTranscriber.kt): only on request (it costs money): "Mitlesen" in the
// recording bar, or someone in the chat wants to read along. Until then the last two minutes are
// kept, so a late start still shows what was just said. PCM 16 kHz (the mean of every three
// 48 kHz samples) in 100 ms chunks to AssemblyAI's streaming socket the server hands out; the
// final words go with the message, so the server need not transcribe again.

import type { LiveSessionResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";

const RATE = 16_000;
const RATIO = 3;
const CHUNK = RATE / 10;
const MAX_BUFFER = 2 * 60 * RATE * 2;

export type LiveStatus = "idle" | "connecting" | "live" | "finishing" | "done" | "failed" | "unavailable";

export interface Turn {
  order: number;
  text: string;
  final: boolean;
}

export interface Word {
  text: string;
  start: number;
  end: number;
}

interface AssemblyMessage {
  type: string;
  turn_order?: number;
  end_of_turn?: boolean;
  transcript?: string;
  words?: Word[];
  session_duration_seconds?: number;
  audio_duration_seconds?: number;
}

export class LiveTranscript {
  status: LiveStatus = "idle";
  readonly turns = new Map<number, Turn>();
  private readonly finalWords = new Map<number, Word[]>();
  private socket: WebSocket | null = null;
  private open = false;
  private started = false;
  private ended = false;
  private terminated = false;
  private overflowed = false;
  private sessionId: string | undefined;
  private chunks: Uint8Array<ArrayBuffer>[] = [];
  private buffered = 0;
  private pending = new Int16Array(CHUNK);
  private pendingCount = 0;
  private acc = 0;
  private accCount = 0;
  private resolveDone: ((complete: boolean) => void) | null = null;
  private readonly done: Promise<boolean>;
  private readonly session: Session;
  /** Status or turns changed; for the panel and the frames to the chat. */
  onChange: (turn?: Turn) => void = () => undefined;

  constructor(session: Session) {
    this.session = session;
    this.done = new Promise((resolve) => (this.resolveDone = resolve));
  }

  /** A 20 ms block of the recording at 48 kHz. */
  feed(pcm: Float32Array) {
    for (const s of pcm) {
      this.acc += Math.max(-1, Math.min(1, s));
      if (++this.accCount === RATIO) {
        this.pending[this.pendingCount++] = Math.round((this.acc / RATIO) * 32767);
        this.acc = 0;
        this.accCount = 0;
        if (this.pendingCount === CHUNK) this.flush();
      }
    }
  }

  /** Begins streaming (from the buffered last two minutes); once. */
  async start(): Promise<void> {
    if (this.started || this.ended) return;
    this.started = true;
    this.set("connecting");
    let live: LiveSessionResponse;
    try {
      live = await this.session.call<LiveSessionResponse>("/v1/transcription/live-session", { method: "POST" });
    } catch {
      return this.fail("unavailable");
    }
    this.sessionId = live.session_id ?? undefined;
    const socket = new WebSocket(live.websocket_url);
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    socket.onopen = () => {
      this.open = true;
      for (const c of this.chunks) socket.send(c);
      this.chunks = [];
      this.buffered = 0;
      if (this.ended) this.terminate();
      this.set(this.ended ? "finishing" : "live");
    };
    socket.onmessage = (event) => this.onMessage(event.data);
    socket.onerror = () => this.fail("failed");
    socket.onclose = () => this.fail("failed");
  }

  /** The recording ended: send the rest and wait for the last words (finish()). */
  end() {
    this.ended = true;
    if (!this.started) {
      this.chunks = [];
      this.resolveDone?.(false);
      return;
    }
    this.flush();
    if (this.open) this.terminate();
    if (this.status === "live" || this.status === "connecting") this.set("finishing");
  }

  /** The words for the message, or null when they are incomplete (the server transcribes then). */
  async finish(timeoutMs = 6_000): Promise<Word[] | null> {
    const complete = await Promise.race([this.done, new Promise<boolean>((r) => setTimeout(() => r(false), timeoutMs))]);
    this.close();
    if (!complete || this.overflowed) return null;
    return [...this.finalWords.entries()].sort((a, b) => a[0] - b[0]).flatMap(([, words]) => words);
  }

  discard() {
    this.close();
    this.resolveDone?.(false);
  }

  private flush() {
    if (this.pendingCount === 0) return;
    const bytes = new Uint8Array(this.pending.buffer.slice(0, this.pendingCount * 2));
    this.pendingCount = 0;
    if (this.open) {
      this.socket!.send(bytes);
    } else if (!this.started) {
      this.chunks.push(bytes);
      this.buffered += bytes.length;
      while (this.buffered > MAX_BUFFER) {
        this.buffered -= this.chunks.shift()!.length;
        // The beginning is gone: the live text is for reading along, the server transcribes the whole.
        this.overflowed = true;
      }
    } else if (this.buffered < MAX_BUFFER) {
      this.chunks.push(bytes);
      this.buffered += bytes.length;
    } else {
      this.overflowed = true;
    }
  }

  private terminate() {
    if (this.terminated) return;
    this.terminated = true;
    this.socket?.send(JSON.stringify({ type: "Terminate" }));
  }

  private onMessage(data: unknown) {
    if (typeof data !== "string") return;
    let message: AssemblyMessage;
    try {
      message = JSON.parse(data) as AssemblyMessage;
    } catch {
      return;
    }
    if (message.type === "Turn" && message.turn_order !== undefined) {
      const words = message.words ?? [];
      const final = message.end_of_turn === true;
      const text = final && message.transcript?.trim() ? message.transcript : words.map((w) => w.text).join(" ");
      const turn = { order: message.turn_order, text, final };
      this.turns.set(turn.order, turn);
      if (final) this.finalWords.set(turn.order, formatted(words, message.transcript));
      this.onChange(turn);
    } else if (message.type === "Termination") {
      const seconds = message.session_duration_seconds ?? message.audio_duration_seconds;
      if (this.sessionId && seconds !== undefined) {
        void this.session.call(`/v1/transcription/live-session/${this.sessionId}/end`, { method: "POST", json: { session_ms: Math.round(seconds * 1000) } }).catch(() => undefined);
      }
      this.set("done");
      this.resolveDone?.(true);
      this.close();
    } else if (message.type === "Error") {
      this.fail("failed");
    }
  }

  private fail(status: LiveStatus) {
    if (this.status === "done") return;
    this.set(status);
    this.resolveDone?.(false);
  }

  private close() {
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      socket.onerror = null;
      try {
        socket.close(1000);
      } catch {
        // Closing anyway.
      }
    }
  }

  private set(status: LiveStatus) {
    this.status = status;
    this.onChange();
  }
}

/** The words with the punctuation of the formatted turn, when both line up word by word (as the app). */
function formatted(words: Word[], transcript: string | undefined): Word[] {
  const tokens = transcript?.trim().split(/\s+/) ?? [];
  return words.map((w, i) => ({ text: tokens.length === words.length ? tokens[i]! : w.text, start: w.start, end: w.end }));
}
